import { useEffect, useState } from 'react';
import { X, Send, MessageCircle, Copy, Smartphone } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchContactsRoles, decompte, nomTiers, publierCompte } from '../../lib/dataProvider';
import { lienCompte } from '../../lib/portail';
import { construireMessage, envoyerSms, getSmsConfig, lienSms, lienWhatsapp } from '../../lib/sms';
import { fmt } from '../shared/badges';

// Envoi du lien de suivi au client : automatique si un fournisseur SMS est
// configuré, sinon partage manuel (SMS du téléphone ou WhatsApp).
export const PartageLienDialog = ({ reception, onClose }) => {
  const r = reception;
  const c = decompte(r);
  const [tel, setTel] = useState('');
  const cfg = getSmsConfig();
  const lien = lienCompte(r.tiersId);
  const message = construireMessage({
    lot: r.id,
    poids: fmt(c.poidsNet),
    lien,
    nom: nomTiers(r.tiersId),
  });

  useEffect(() => {
    publierCompte(r.tiersId);
    fetchContactsRoles().then((rows) => setTel(rows.find((x) => x.id === r.tiersId)?.tel ?? ''));
  }, [r.tiersId]);

  const envoyer = async () => {
    if (!tel.trim()) return toast.error('Renseignez le numéro du client.');
    const { error } = await envoyerSms({ destinataire: tel.trim(), message });
    if (error) return toast.warning(error);
    toast.success(`Lien de suivi envoyé au ${tel.trim()}`);
    onClose();
  };

  return (
    <div data-testid="partage-lien-overlay" className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4">
      <div data-testid="partage-lien-dialog" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <Send size={18} /> Envoyer le lien de suivi
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Lot {r.id} · {nomTiers(r.tiersId)}
            </p>
          </div>
          <button type="button" data-testid="partage-fermer" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Numéro du client</label>
            <input
              data-testid="partage-tel"
              value={tel}
              onChange={(e) => setTel(e.target.value)}
              placeholder="+216 ..."
              className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Message</label>
            <p data-testid="partage-message" className="mt-1.5 border border-slate-200 bg-slate-50 px-3 py-2 font-data text-xs text-slate-700">
              {message}
            </p>
          </div>

          <p
            data-testid="partage-statut-fournisseur"
            className={`border-l-2 px-3 py-2 text-xs ${cfg.actif && cfg.url && cfg.token ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-amber-500 bg-amber-50 text-amber-800'}`}
          >
            {cfg.actif && cfg.url && cfg.token
              ? `Envoi automatique actif via ${cfg.fournisseur}.`
              : `Fournisseur SMS (${cfg.fournisseur}) pas encore configuré — utilisez le partage manuel ci-dessous ou renseignez les identifiants dans Administration.`}
          </p>

          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="partage-envoyer-sms" onClick={envoyer} className="flex h-11 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700">
              <Send size={15} /> Envoi automatique
            </button>
            <a
              href={lienWhatsapp(tel, message)}
              target="_blank"
              rel="noreferrer"
              data-testid="partage-whatsapp"
              className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a
              href={lienSms(tel, message)}
              data-testid="partage-sms-tel"
              className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Smartphone size={15} /> SMS du téléphone
            </a>
            <button
              type="button"
              data-testid="partage-copier"
              onClick={() => {
                navigator.clipboard?.writeText(lien);
                toast.success('Lien copié');
              }}
              className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Copy size={15} /> Copier le lien
            </button>
          </div>

          <p className="break-all font-data text-[11px] text-slate-500">{lien}</p>
        </div>
      </div>
    </div>
  );
};
